import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Eye, Star, ClipboardCheck } from 'lucide-react';

const InspectionList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cleaning-inspections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load inspections');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = (inspection) => {
    setSelectedInspection(inspection);
    setShowDetailDialog(true);
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return labels[rating] || 'N/A';
  };

  const getRatingColor = (rating) => {
    if (rating === 5) return 'text-green-600';
    if (rating === 4) return 'text-blue-600';
    if (rating === 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg">Loading inspections...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <ClipboardCheck className="h-8 w-8" style={{ color: '#6B9F3E' }} />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              My Inspections / Mis Inspecciones
            </h2>
          </div>
          <p className="text-sm text-slate-600">
            View your inspection submissions / Ver sus envíos de inspección
          </p>
        </div>
        <Button
          onClick={() => navigate('/field/inspections/template-based')}
          style={{ backgroundColor: '#6B9F3E' }}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Inspection / Nueva Inspección
        </Button>
      </div>

      <div className="space-y-3">
        {inspections.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardCheck className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No inspections yet.</p>
              <p className="text-slate-500 text-sm">Aún no hay inspecciones.</p>
              <Button
                onClick={() => navigate('/field/inspections/new')}
                className="mt-4"
                style={{ backgroundColor: '#6B9F3E' }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Inspection / Crear Primera Inspección
              </Button>
            </CardContent>
          </Card>
        ) : (
          inspections.map((inspection) => (
            <Card 
              key={inspection.id} 
              className="hover:shadow-md transition-shadow"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-base">{inspection.inspection_number}</CardTitle>
                      <Badge variant="outline">{inspection.service_type || 'Cleaning'}</Badge>
                    </div>
                    <p className="text-sm text-slate-600">
                      {inspection.location_name}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => viewDetails(inspection)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Date / Fecha:</span>
                    <span className="font-medium">{_fmtDate(inspection.inspection_date)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Inspector:</span>
                    <span className="font-medium">{inspection.inspector_name}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Rating / Calificación:</span>
                    <div className="flex items-center gap-2">
                      <Star className={`h-4 w-4 ${getRatingColor(inspection.cleanliness_rating)}`} fill="currentColor" />
                      <span className={`font-semibold ${getRatingColor(inspection.cleanliness_rating)}`}>
                        {inspection.cleanliness_rating} - {getRatingLabel(inspection.cleanliness_rating)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Submitted / Enviado:</span>
                    <span className="text-xs">{_fmtDate(inspection.submitted_at)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inspection Details / Detalles de Inspección</DialogTitle>
          </DialogHeader>
          {selectedInspection && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Inspection Number</Label>
                  <p className="font-bold">{selectedInspection.inspection_number}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Service Type</Label>
                  <p className="font-medium">{selectedInspection.service_type || 'Cleaning'}</p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Location</Label>
                <p className="font-medium">{selectedInspection.location_name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Date</Label>
                  <p className="font-medium">{_fmtDate(selectedInspection.inspection_date)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Inspector</Label>
                  <p className="font-medium">{selectedInspection.inspector_name}</p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Rating</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Star className={`h-5 w-5 ${getRatingColor(selectedInspection.cleanliness_rating)}`} fill="currentColor" />
                  <span className={`font-semibold ${getRatingColor(selectedInspection.cleanliness_rating)}`}>
                    {selectedInspection.cleanliness_rating} - {getRatingLabel(selectedInspection.cleanliness_rating)}
                  </span>
                </div>
              </div>

              {selectedInspection.notes && (
                <div>
                  <Label className="text-xs text-slate-500">Notes / Comments</Label>
                  <p className="mt-1 p-3 bg-gray-50 rounded border">{selectedInspection.notes}</p>
                </div>
              )}

              {selectedInspection.issues_found && (
                <div>
                  <Label className="text-xs text-slate-500">Issues Found</Label>
                  <p className="mt-1 p-3 bg-orange-50 rounded border border-orange-200">{selectedInspection.issues_found}</p>
                </div>
              )}

              {selectedInspection.corrective_actions && (
                <div>
                  <Label className="text-xs text-slate-500">Corrective Actions</Label>
                  <p className="mt-1 p-3 bg-blue-50 rounded border border-blue-200">{selectedInspection.corrective_actions}</p>
                </div>
              )}

              {selectedInspection.photos && selectedInspection.photos.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500 mb-2">Photos ({selectedInspection.photos.length})</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedInspection.photos.map((photo, idx) => (
                      <img key={idx} src={photo} alt={`Photo ${idx + 1}`} className="w-full h-32 object-cover rounded" />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-500">Submitted At</Label>
                <p className="text-sm">{_fmtDateTime(selectedInspection.submitted_at)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close / Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InspectionList;
